// src/i18n/en/index.ts

import raEnglishMessages from 'ra-language-english';
import summary from './summary';
import simulators from './simulators';
import users from './users';
import devices from './devices';
import kioskSimulator from './kioskSimulator';
import alerts from './alerts';
import groups from './groups';
import hotels from './hotels';
import activityMonitor from './activityMonitor';
import activityMonitorMetrics from './activityMonitorMetrics';
import activityMonitorAdditional from './activityMonitorAdditional';
import errorMonitor from './errorMonitor';
import help from './help';
import errorDefinitions from './errorDefinitions';
import events from './events';
import adminOptions from './adminOptions';
import system from './system';
import logs from './logs';
import labels from './labels';
import channels from './channels';
import scope from './scope';
import preferences from './preferences';
import options from './options';
import reservationStates from './reservationStates';
import arrivals from './arrivals';
import reservations from './reservations';
import wall from './wall';
import channelsAnalytics from './channelsAnalytics';
import dispensers from './dispensers';
import durations from './durations';
import payments from './payments';
import hotelsOverview from './hotelsOverview';
import operacion from './operacion';
import liveBoard from './liveBoard';
import notAuthorized from './notAuthorized';
import insightV2 from './insightV2';
import reconciliation from './reconciliation';
import ask from './ask';
import control from './control';
import status from './status';
import benchmark from './benchmark';
import fit from './fit';
import satisfaction from './satisfaction';
import manual from '@/features/manual/i18n/en';
import assistantConnect from '@/features/assistant-connect/i18n/en';
import releaseNotes from '@/features/release-notes/i18n/en';
import developers from '@/features/developers/i18n/en';

/**
 * English translations
 * Import all feature translations and combine them
 */
const en = {
  ...raEnglishMessages,
  
  ra: {
    ...raEnglishMessages.ra,
    action: {
      ...raEnglishMessages.ra.action,
      next: 'Next',
      back: 'Back',
      clear_input_value: 'Clear',
      export: 'Export',
      expand: 'Expand',
      close: 'Close',
      confirm: 'Confirm',
      loading: 'Loading...',
      menu: 'Menu',
      search: 'Search',
    },
    navigation: {
      ...raEnglishMessages.ra.navigation,
      page_rows_per_page: 'Rows per page:',
    },
    sort: {
      ...raEnglishMessages.ra.sort,
      ASC: 'Ascending',
    },
    configurable: {
      ...raEnglishMessages.ra.configurable,
      customize: 'Customize',
    },
    message: {
      ...raEnglishMessages.ra.message,
      // The not-found page adds the final full stop itself.
      not_found: 'Either you typed a wrong URL, or you followed a bad link',
      error: 'An error occurred',
      delete_title: 'Delete %{name}',
      delete_content: 'Are you sure you want to delete this item?',
      bulk_delete_title: 'Delete %{name} |||| Delete %{smart_count} %{name}',
      bulk_delete_content: 'Are you sure you want to delete this %{name}? |||| Are you sure you want to delete these %{smart_count} items?',
    },
    page: {
      ...raEnglishMessages.ra.page,
      error: 'Something went wrong',
      empty: 'No items yet',
      invite: 'Add one to get started',
    },
    auth: {
      ...raEnglishMessages.ra.auth,
      logging_in: 'Logging in...',
    },
  },
  
  resources: {
    summary,
    operacion,
    simulators,
    users,
    devices,
    'kiosk-simulator': kioskSimulator,
    'activity-monitor': activityMonitor,
    'error-monitor': errorMonitor,
    alerts,
    hotels,
    groups,
    activityMonitor,
    activityMonitorMetrics,
    activityMonitorAdditional,
    errorMonitor,
    abandonmentAnalysis: errorMonitor.abandonmentAnalysis,
    help,
    errorDefinitions,
    'error-definitions': errorDefinitions,
    events,
    adminOptions,
    'admin-options': adminOptions,
    system,
    logs,
    labels,
    channels,
    reservationStates,
    arrivals,
    reservations,
    channelsAnalytics,
    dispensers,
    durations,
    payments,
    hotelsOverview,
    liveBoard,
    notAuthorized,
    insightV2,
    reconciliation,
    ask,
    control,
    status,
    benchmark,
    fit,
    satisfaction,
  },

  // Multi-hotel scope selector (Decision 6/§1 of the redesign)
  scope,

  // User preferences store + «My preferences» page (Decision 8/§10)
  preferences,

  // In-app user manual (`/manual`, «Ver en el manual» in page headers)
  manual,

  // «Conecte su asistente de IA» (`/assistant`, app-bar entry)
  assistantConnect,
  // In-app release notes («Novedades»)
  releaseNotes,
  // Developers section (/developers): the public API documentation and, for staff, its keys
  developers,

  // «Opciones del hotel» / «Opciones de Roommatik» (mockup 13, tabs 2-3)
  options,

  // Wall screen (mockup 02, `/#/panel?hotel=<id>`)
  wall,

  // Common translations used across the app
  common: {
    locale: 'en',
    // Figure withheld below the minimum sample (Tiempos, Canales).
    sample: {
      insufficient: 'Insufficient sample (n = %{count})',
    },
    export: {
      inProgress: 'Exporting…',
      failed: 'The export could not be completed. Please try again.',
    },
    exportFiles: {
      arrivals: 'arrivals',
      benchmark: 'benchmark',
      channels: 'channels',
      control: 'control',
      durations: 'durations',
      instalaciones: 'properties',
      operacion: 'operations',
      payments: 'payments',
      reconciliation: 'reconciliation',
      reservations: 'reservations',
      summary: 'summary',
    },
    all: 'All',
    notAssigned: 'Not assigned',
    linked: 'Linked',
    registryLinkOptional: 'Optional. Choose the matching registry record; once saved it cannot be changed from here.',
    registryLinkLocked: 'Linked to the registry. The link cannot be changed from here.',
    never: 'Never',
    no_data_title: 'No Data Available',
    no_data_description: 'There is currently no data to display.',

    // Generic terminology: the product runs in hotels AND on ships, so user-facing
    // text must avoid "hotel" / "room". Use installation / unit instead.
    terms: {
      property: 'Property',
      unit: 'Unit',
      cabin: 'Cabin',
      site: 'Site',
    },

    actions: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      deleting: 'Deleting…',
      edit: 'Edit',
      create: 'Create',
      close: 'Close',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      search: 'Search',
      filter: 'Filter',
      clear: 'Clear',
      export: 'Export',
      import: 'Import',
      refresh: 'Refresh',
      view: 'View',
      download: 'Download',
      retry: 'Retry',

    },
    
    labels: {
      yes: 'Yes',
      no: 'No',
      all: 'All',
      none: 'None',
      loading: 'Loading...',
      saving: 'Saving...',
      deleting: 'Deleting...',
      processing: 'Processing...',
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
      metadata: 'Metadata',
      items_count: '%{count} item |||| %{count} items',
      devices: 'Devices',
      optional: 'Optional',
    },
    
    messages: {
      confirm_delete: 'Are you sure you want to delete this item?',
      delete_success: 'Item deleted successfully',
      delete_error: 'Error deleting item',
      save_success: 'Changes saved successfully',
      save_error: 'Error saving changes',
      required_field: 'This field is required',
      invalid_email: 'Invalid email address',
      no_results: 'No results found',
      no_data: 'No data available',
      load_error: 'Could not load the information. Please try again.',
    },
    
    notifications: {
      session_extend_error: 'Failed to extend session. Please login again.',
      session_extended_with_time: 'Session extended. New expiration: %{time}',
    },

    // Shared period picker (Resumen, Tiempos, Cobros, Hoteles)
    period: {
      from: 'From',
      to: 'To',
      compare: 'Compare with the previous period',
    },
    
    navigation: {
      home: 'Home',
      dashboard: 'Dashboard',
      settings: 'Settings',
      profile: 'Profile',
      logout: 'Logout',
      help: 'Help',
    },
    
    filters: {
      label: 'Filters',
      show: 'Show filters',
      hide: 'Hide filters',
    },

    help: {
      tooltip: 'Help about this page',
      close: 'Close',
      watchVideo: 'Watch tutorial video',
      features: 'Key Features',
      keyFeatures: 'Key Features',
      useCases: 'Use Cases',
      tips: 'Tips and Tricks',
      quickTips: 'Quick Tips',
      ...help,
    },
  },
  
  // Application shell (sidebar groups, environment pill)
  shell: {
    menu: {
      groups: {
        reception: 'Reception',
        direction: 'Management',
        finance: 'Finance',
        incidents: 'Incidents',
        systems: 'Systems',
        administration: 'Administration',
        help: 'Help',
      },
    },
    sections: {
      label: 'Sections',
    },
    environment: {
      nonProduction: 'Test',
    },
    impersonation: {
      active: 'Impersonating %{name}',
      exit: 'Exit',
    },
    export: {
      button: 'Export',
      generating: 'Generating export…',
      empty: 'No rows to export',
      error: 'Could not generate the export',
    },
    subMenu: {
      scrollLeft: 'Show previous tabs',
      scrollRight: 'Show more tabs',
    },
  },

  // Auth related translations
  auth: {
    login: 'Login',
    logout: 'Logout',
    username: 'Username',
    password: 'Password',
    email: 'Email',
    remember_me: 'Remember me',
    forgot_password: 'Forgot password',
    login_error: 'Login error',
    invalid_credentials: 'Invalid credentials',
    session_expired: 'Your session has expired',
    unauthorized: 'Unauthorized',
    session_expiry: {
      title: 'Session Expiring Soon',
      message: 'Your session is about to expire',
      time_remaining: 'Time remaining',
      info: 'You will be automatically logged out when the session expires',
      extend: 'Extend Session',
    },
  },
  
  // Validation messages
  validation: {
    required: 'This field is required',
    email: 'Must be a valid email address',
    minLength: 'Must be at least %{min} characters',
    maxLength: 'Cannot be more than %{max} characters',
    minValue: 'Must be at least %{min}',
    maxValue: 'Cannot be greater than %{max}',
    pattern: 'Invalid format',
    unique: 'This value already exists',
  },
  
  // Pages translations
  pages: {
    login: {
      subtitle: 'Login to start',
      login_button: 'Login',
      forgot_password: 'Forgot your password?',
      or: 'or',
      keycloak_button: 'Sign in with Roommatik Auth',
      keycloak_loading: 'Connecting to Roommatik Auth...',
      keycloak_processing: 'Processing authentication...',
      keycloak_error: 'Roommatik Auth authentication failed',
    },
    forgot_password: {
      title: 'Forgot Password',
      subtitle: 'Enter your email and we will send you a link to reset your password',
      email_label: 'Email address',
      submit_button: 'Send Reset Link',
      sending: 'Sending...',
      back_to_login: 'Back to Login',
      email_sent: 'Reset link sent successfully',
      success_message: 'If the email is registered, you will receive a link to reset your password. Please check your inbox.',
      error: 'An error occurred. Please try again.',
    },
    reset_password: {
      title: 'Reset Password',
      new_password: 'New Password',
      confirm_password: 'Confirm Password',
      submit_button: 'Reset Password',
      resetting: 'Resetting...',
      back_to_login: 'Back to Login',
      success: 'Password reset successfully',
      success_message: 'Your password has been reset. You will be redirected to login shortly.',
      redirecting: 'Redirecting to login...',
      error: 'An error occurred. Please try again.',
      no_token: 'No reset token provided. Please use the link from your email.',
      invalid_token: 'The reset link is invalid or has expired.',
      validation_error: 'Could not validate the reset link. Please try again.',
      validating: 'Validating link...',
      passwords_dont_match: 'Passwords do not match',
      password_too_short: 'Password must be at least 6 characters',
    },
  },

  // Public shareable monitor panel (viewer-facing)
  panel: {
    view: {
      activity: 'Activity monitor',
      devices: 'Devices',
    },
    unavailable: {
      title: 'This panel is not available',
      description: 'The link may have expired or been revoked. Ask an administrator for a new one.',
    },
  },

  // Shareable panel link management (admin-facing)
  panelLinks: {
    create: 'Create panel link',
    dialog: {
      title: 'Create panel link',
      view: 'View',
      viewActivity: 'Activity monitor',
      viewDevices: 'Devices',
      scope: 'Scope',
      scopeGroup: 'Group',
      scopeHotel: 'Property',
      selectScope: 'Select a scope',
      timeWindow: 'Time window',
      window: {
        '1': 'Last hour',
        '6': 'Last 6 hours',
        '12': 'Last 12 hours',
        '24': 'Last 24 hours',
        '48': 'Last 2 days',
        '72': 'Last 3 days',
        '168': 'Last 7 days',
      },
      layout: 'Layout',
      layoutGrid: 'Grid',
      layoutColumns: 'Columns per device',
      showLabels: 'Show labels',
      duration: 'Duration',
      duration24h: 'Temporary (24 hours)',
      durationPermanent: 'Permanent display (revocable)',
      label: 'Name (optional)',
      labelHelp: 'For example, "Reception TV"',
      submit: 'Create link',
      cancel: 'Cancel',
    },
    result: {
      title: 'Link created',
      description: 'Share this address or the QR code. It will not be shown again.',
      copy: 'Copy link',
      copied: 'Link copied',
      done: 'Done',
    },
    list: {
      title: 'Active links',
      empty: 'No active links.',
      view: 'View',
      scope: 'Scope',
      expires: 'Expires',
      permanent: 'Permanent',
      lastSeen: 'Last seen',
      createdBy: 'Created by',
      revoke: 'Revoke',
      revoked: 'Link revoked',
      regenerateHelp: 'Get the link & QR again (the previous link will stop working)',
      open: 'Open',
      never: '—',
    },
    created: 'Panel link created successfully',
    regenerated: 'A new link was generated. The previous one is no longer valid.',
    error: 'The operation could not be completed',
  },
};

export default en;